import * as Sentry from "@sentry/react";
import { Disease } from "shared/model/diseases";
import { FrontendPageEnum } from "shared/model/pages";

/**
 * MARK: tab_click
 */
export interface GtmEventTabClickYourStory {
  target: "your_story";
  disease: Disease;
  bottomNavigation: boolean;
  node_type?: string | null;
  node_subtype?: string | null;
  node_id?: string | null;
}

export interface GtmEventTabClickYourQuestion {
  target: "your_questions";
  disease: Disease;
  bottomNavigation: boolean;
  node_type?: string | null;
  node_subtype?: string | null;
  node_id?: string | null;
}

export interface GtmEventTabClickSummary {
  target: "summary";
  disease: Disease;
  bottomNavigation: boolean;
  node_type?: string | null;
  node_subtype?: string | null;
  node_id?: string | null;
}

export interface GtmEventTabClick {
  eventName: "tab_click";
  parameters:
    | GtmEventTabClickYourStory
    | GtmEventTabClickYourQuestion
    | GtmEventTabClickSummary;
}

/**
 * MARK: tab_view
 */
export interface GtmEventTabViewYourStory {
  target: "your_story";
  disease: Disease;
  bottomNavigation: boolean;
  node_type?: string | null;
  node_subtype?: string | null;
  node_id?: string | null;
}

export interface GtmEventTabViewYourQuestion {
  target: "your_questions";
  disease: Disease;
  bottomNavigation: boolean;
  node_type?: string | null;
  node_subtype?: string | null;
  node_id?: string | null;
}

export interface GtmEventTabViewSummary {
  target: "summary";
  disease: Disease;
  bottomNavigation: boolean;
  node_type?: string | null;
  node_subtype?: string | null;
  node_id?: string | null;
}

export interface GtmEventTabView {
  eventName: "tab_view";
  parameters:
    | GtmEventTabViewYourStory
    | GtmEventTabViewYourQuestion
    | GtmEventTabViewSummary;
}

/**
 * MARK: your story
 */
export interface GtmEventAudioRecordStart {
  eventName: "audio_record_start";
  parameters: {
    disease: Disease;
    node_type?: string | null;
    node_subtype?: string | null;
    node_id?: string | null;
  };
}

export interface GtmEventAudioMessageSent {
  eventName: "audio_message_sent";
  parameters: {
    disease: Disease;
    node_type?: string | null;
    node_subtype?: string | null;
    node_id?: string | null;
    voice_duration: number;
  };
}

export interface GtmEventTextMessageSent {
  eventName: "text_message_sent";
  parameters: {
    disease: Disease;
    node_type?: string | null;
    node_subtype?: string | null;
    node_id?: string | null;
    file_attached: boolean;
  };
}

export interface GtmEventAttachmentClicked {
  eventName: "attachment_clicked";
  parameters: {
    disease: Disease;
    node_type?: string | null;
    node_subtype?: string | null;
    node_id?: string | null;
  };
}

export type GtmEventYourStory =
  | GtmEventAudioRecordStart
  | GtmEventAudioMessageSent
  | GtmEventTextMessageSent
  | GtmEventAttachmentClicked;

/**
 * MARK: miscellaneous
 */
export interface GtmEventSignupStarted {
  eventName: "signup_started";
  parameters?: Record<string, string | number | boolean | null | undefined>;
}

export interface GtmEventAccountCreated {
  eventName: "account_created";
  parameters?: Record<string, string | number | boolean | null | undefined>;
}

export interface GtmEventAccountConfirmed {
  eventName: "account_confirmed";
  parameters?: Record<string, string | number | boolean | null | undefined>;
}

export interface GtmEventSignupFinished {
  eventName: "signup_finished";
  parameters?: Record<string, string | number | boolean | null | undefined>;
}

export interface GtmEventFormTouched {
  eventName: "signup_form_touched";
  parameters?: Record<string, string | number | boolean | null | undefined>;
}

export interface GtmEventUserId {
  eventName: "user_id";
  parameters: {
    user_id: string;
  };
}

export interface GtmEventAppOpen {
  eventName: "app_open";
  parameters: {
    source: "magic-link" | "otp";
  };
}

export type GtmEventOnce =
  | GtmEventSignupStarted
  | GtmEventAccountCreated
  | GtmEventAccountConfirmed
  | GtmEventSignupFinished
  | GtmEventFormTouched
  | GtmEventUserId
  | GtmEventAppOpen;

/**
 * MARK: entrypoint
 */
export type GtmEvent =
  | GtmEventTabClick
  | GtmEventTabView
  | GtmEventYourStory
  | GtmEventOnce;

/**
 * MARK: routes
 */

const gtmRelevantRoutes: {
  [FrontendPageEnum.CHAT]: GtmEventTabClickYourStory["target"];
  [FrontendPageEnum.MAMA_ANSWER]: GtmEventTabClickYourQuestion["target"];
  [FrontendPageEnum.REPORT]: GtmEventTabClickSummary["target"];
} = {
  [FrontendPageEnum.CHAT]: "your_story",
  [FrontendPageEnum.MAMA_ANSWER]: "your_questions",
  [FrontendPageEnum.REPORT]: "summary",
};

export const getGtmRouteTarget = (
  pathname: string,
): (typeof gtmRelevantRoutes)[keyof typeof gtmRelevantRoutes] | null => {
  const found = Object.entries(gtmRelevantRoutes).find(([route]) =>
    pathname.endsWith(route),
  );
  if (!found) {
    Sentry.captureException(
      new Error(`Coult not map path ${pathname} to any expected gtm event`),
      {
        level: "error",
      },
    );
    return null;
  }
  return found[1];
};
